import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Briefcase, Globe, MapPin, ChevronLeft, ShieldCheck } from "lucide-react";

import { fetchBusiness } from "@/lib/api";

export default function VoorBedrijvenProfiel() {
  const { slug } = useParams();
  const [biz, setBiz] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchBusiness(slug).then(setBiz).catch(() => setError("Dit bedrijfsprofiel is niet (meer) beschikbaar."));
  }, [slug]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-display text-3xl font-bold text-ink">{error}</h1>
        <Link to="/voor-bedrijven" className="inline-block mt-6 text-[#002FA7] font-semibold">← Terug naar B2B-overzicht</Link>
      </div>
    );
  }
  if (!biz) {
    return <div className="max-w-3xl mx-auto px-6 py-32"><div className="h-8 w-2/3 bg-slate-200 rounded animate-pulse" /></div>;
  }

  return (
    <div className="py-16" data-testid={`biz-profile-${slug}`}>
      <div className="max-w-4xl mx-auto px-6">
        <Link to="/voor-bedrijven" className="inline-flex items-center gap-1 text-sm text-ink-soft hover:text-ink mb-6">
          <ChevronLeft className="h-4 w-4" /> Alle partners
        </Link>

        <div className="flex items-start gap-4 mb-8">
          <div className="h-16 w-16 rounded-2xl bg-[#264653] text-white flex items-center justify-center shadow-lg">
            <Briefcase className="h-8 w-8" />
          </div>
          <div>
            <span className="text-xs uppercase tracking-wider text-[#264653] font-semibold">CheckExpert Partner</span>
            <h1 className="font-display text-4xl font-bold text-ink tracking-tight">{biz.business_name}</h1>
            <p className="text-ink-soft mt-1 text-sm">
              {biz.branches?.join(" · ")} · Regio's: {biz.regions?.join(", ")}
            </p>
          </div>
        </div>

        <div className="rounded-3xl bg-white border border-border p-8">
          <h2 className="font-display text-xl font-semibold text-ink">Over ons</h2>
          <p className="text-ink-soft mt-3 leading-relaxed whitespace-pre-line">{biz.description}</p>

          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {biz.website && (
              <a
                href={biz.website}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="flex items-center gap-3 p-4 rounded-xl bg-secondary hover:bg-slate-100 transition-colors"
              >
                <Globe className="h-5 w-5 text-[#264653]" />
                <div>
                  <div className="text-xs uppercase tracking-wider text-ink-subtle">Website</div>
                  <div className="text-ink font-semibold text-sm truncate">{biz.website}</div>
                </div>
              </a>
            )}
            <div className="flex items-center gap-3 p-4 rounded-xl bg-secondary">
              <MapPin className="h-5 w-5 text-[#264653]" />
              <div>
                <div className="text-xs uppercase tracking-wider text-ink-subtle">Regio's</div>
                <div className="text-ink font-semibold text-sm">{biz.regions?.join(", ")}</div>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-border flex items-center justify-between flex-wrap gap-3">
            <p className="text-xs text-ink-subtle inline-flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5 text-[#10B981]" /> CheckExpert verifieerde dit profiel
            </p>
            <Link to="/voor-bedrijven" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-border text-ink text-sm font-semibold hover:border-ink-soft">
              Word ook partner →
            </Link>
          </div>
        </div>

        <p className="text-xs text-ink-subtle mt-8 italic max-w-2xl text-center mx-auto">
          CheckExpert linkt particulieren door naar dit bedrijf op basis van branche en regio. Dit is geen aanbeveling van CheckExpert zelf — controleer altijd zelf de credentials en lees onafhankelijke reviews.
        </p>
      </div>
    </div>
  );
}
